// @flow
import React, {
  useRef,
  useState,
} from 'react';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import Button from '@atlaskit/button';
import * as utils from 'chronos-utils';

import info from 'chronos-assets/images/info.svg';
import settings from 'chronos-assets/images/settings.svg';
import download from 'chronos-assets/images/download.svg';
import help from 'chronos-assets/images/help.svg';
import clock from 'chronos-assets/images/clock.svg';

import {
  Flex,
} from 'chronos-styled';
import {
  ClockIcon,
  LinkA,
  LinkImg,
  LinkText,
} from './styled';
import InfoModal from './InfoModal';
import SettingsServerModal from './SettingsServerModal';

type Props = {
  worklogs: any,
  fetching: boolean,
  calendarDate: any,
  /**
   * Selected Days Count
   */
  selectedDaysCount?: number,
  /**
   * Selected Rows Count
   */
  selectedRowsCount?: number,
  /**
   * Handler to be called on click remove.
   */
  clearSelectedDays?: { (): void },
  /**
   * Handler to be called on click remove.
   */
  clearTimelineSelection?: { (): void },
  jiraType: 'server' | 'cloud',
  showGuide: { (): void },
  /**
   * groupBy one of issues, epics, users, projects, versions.
   * */
  groupBy: string,
  /**
   * hours worked
   */
  timeWorked?: number,
  /**
   * visibility of info modal
   */
  isInfoModalVisible: boolean,
  isServerSettingsModalVisible: boolean,
  /**
   * handle of info modal visibility
   */
  setInfoModalVisibility: { (): void },
  setServerSettingsModalVisibility: { (): void },
};

const Footer = ({
  worklogs,
  fetching,
  calendarDate,
  selectedDaysCount,
  selectedRowsCount,
  clearTimelineSelection,
  clearSelectedDays,
  groupBy,
  timeWorked,
  isInfoModalVisible,
  isServerSettingsModalVisible,
  jiraType,
  isSystemAdmin,
  setInfoModalVisibility,
  setServerSettingsModalVisibility,
  showGuide,
}: Props) => {
  const exportLinkRef: ElementRef = useRef(null);

  const generateCSVReport = () => {
    let totalTimeSeconds = 0;
    const re = new RegExp('"', 'g');
    const csvRows = Object.keys(worklogs).map((worklogId) => {
      const worklog = worklogs[worklogId];
      totalTimeSeconds += worklog.timeSpentSeconds;
      return [
        `"${worklog.issue.key}"`,
        `"${worklog.issue.fields.project.name.replace(re, '')}"`,
        `"${worklog.issue.fields.summary.replace(re, '')}"`,
        `"${worklog.issue.fields.priority?.name || '-'}"`,
        `"${worklog.author.displayName}"`,
        worklog.created,
        worklog.timeSpent,
        worklog.timeSpentSeconds,
        (worklog.timeSpentSeconds / 3600).toFixed(3),
      ].join(',');
    });
    const csvHeaders = [
      'Key',
      'Project',
      'Summary',
      'Priority',
      'User',
      'Date started',
      'Time spent',
      'Time spent(s)',
      'Time spent(h)',
    ].join(',');
    const totalTimeHours = (totalTimeSeconds / 3600).toFixed(3);
    const csvString = `${csvHeaders}%0A${csvRows.join('%0A')}%0A,,,,,,${utils.stj(totalTimeSeconds, "h'h' m'm'")},${totalTimeSeconds},${totalTimeHours}`;
    exportLinkRef.current.href = `data:text/csv,${csvString}`;
    exportLinkRef.current.target = '_blank';
    exportLinkRef.current.download = ([
      'ChronosReport_',
      calendarDate.s.toISODate(),
      ...(
        calendarDate.s.toISODate() === calendarDate.e.toISODate()
          ? ([])
          : ([
            '_',
            calendarDate.e.toISODate(),
          ])
      ),
      '.csv',
    ].join(''));
  };

  const [footerVisible, setFooterVisible] = useState(false);

  return (
    <Flex justify="space-between" padding="10px" align="center" borderTop="1px solid #f0f1f2">
      <InfoModal
        isVisible={isInfoModalVisible}
        setVisibility={setInfoModalVisibility}
        showGuide={showGuide}
      />
      {jiraType === 'server' && (
        <SettingsServerModal
          isVisible={isServerSettingsModalVisible}
          setVisibility={setServerSettingsModalVisibility}
          showGuide={showGuide}
        />
      )}
      <Flex spacing={8} align="center">
        {jiraType === 'server' && (
          <Flex>
            <Button
              appearance="subtle-link"
              onClick={() => {
                const page = document.querySelector('div#page');
                page.scrollTop = footerVisible ? 0 : page.scrollHeight;
                setFooterVisible(!footerVisible);
              }}
            >
              {footerVisible ? 'back ↑' : 'jira footer ↓'}
            </Button>
          </Flex>
        )}
        {!fetching
        && (
          <Flex spacing={8} align="center" padding="0 0 0 259px">
            <span style={{ fontWeight: 100, fontSize: 12, opacity: 0.5 }}>
              total time spent
            </span>
            <ClockIcon src={clock} />
            <span style={{ fontWeight: 500, fontSize: 16 }}>
              {utils.stj(timeWorked)}
            </span>
          </Flex>
        )}
        <Flex padding="0px 4px">
          {selectedDaysCount > 0 && (
            <Button
              onClick={clearSelectedDays}
              iconAfter={<EditorCloseIcon />}
            >
              {selectedDaysCount}
              {' '}
              day
              {selectedDaysCount === 1 ? '' : 's'}
              {' '}
              selected
            </Button>
          )}
          {(selectedRowsCount > 0) && (
            <Button
              onClick={clearTimelineSelection}
              iconAfter={<EditorCloseIcon />}
            >
              {selectedRowsCount}
              {' '}
              {selectedRowsCount === 1 ? groupBy.slice(0, -1) : groupBy}
              {' '}
              selected
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex spacing={4} style={{ marginRight: '80px' }}>
        {(
          jiraType === 'server'
          && isSystemAdmin
        ) && (
          <Button
            onClick={() => setServerSettingsModalVisibility(true)}
          >
            <Flex fillSpace align="center" justify="center" spacing={4}>
              <LinkImg src={settings} alt="settings" />
              <LinkText>Settings</LinkText>
            </Flex>
          </Button>
        )}
        <Button
          onClick={() => setInfoModalVisibility(true)}
        >
          <Flex fillSpace align="center" justify="center" spacing={4}>
            <LinkImg src={info} alt="info" />
            <LinkText>Info</LinkText>
          </Flex>
        </Button>
        <LinkA
          innerRef={exportLinkRef}
          onClick={() => {
            generateCSVReport();
          }}
        >
          <Button>
            <Flex fillSpace align="center" justify="center" spacing={4}>
              <LinkImg src={download} alt="export" />
              <LinkText>
                Export
              </LinkText>
            </Flex>
          </Button>
        </LinkA>
        <LinkA href="https://web-pal.atlassian.net/servicedesk/customer/portal/2">
          <Button>
            <Flex fillSpace align="center" justify="center" spacing={4}>
              <LinkImg src={help} alt="support" />
              <LinkText>Support</LinkText>
            </Flex>
          </Button>
        </LinkA>
      </Flex>
    </Flex>
  );
};

Footer.defaultProps = {
  timeWorked: 0,
  selectedDaysCount: 0,
  selectedRowsCount: 0,
  selectedColumnsCount: 0,
  clearSelectedDays: () => {},
  clearTimelineSelection: () => {},
};

export default Footer;
